import {
  ChevronDown,
  KeyRound,
  LogOut,
  Menu,
  PanelLeftClose,
  PanelLeftOpen,
  ShieldCheck,
  UserCog,
} from 'lucide-react';
import { useCallback, useState } from 'react';
import { useLocation } from 'react-router-dom';

import { Avatar } from '@/components/ui/avatar';
import { BotonPantallaCompleta } from '@/components/ui/boton-pantalla-completa';
import { useDismissable } from '@/hooks/use-dismissable';
import { resolvePageTitle } from '@/config/navigation';
import { useLogout } from '@/features/auth/hooks/use-logout';
import { useAuthStore } from '@/features/auth/auth.store';
import { CambiarContrasenaDialog } from '@/features/auth/components/cambiar-contrasena-dialog';
import { NotificacionesMenu } from '@/features/notificaciones/components/notificaciones-menu';
import { cn } from '@/lib/utils';

interface TopbarProps {
  onOpenSidebar: () => void;
  /** Menú lateral contraído (sólo tiene efecto en escritorio). */
  contraido: boolean;
  onToggleSidebar: () => void;
}

/**
 * Barra superior: título de la pantalla, notificaciones y menú de usuario.
 *
 * En el mismo sitio donde el móvil abre el cajón, el escritorio **contrae o
 * despliega** el menú lateral (la preferencia se recuerda, ver
 * `useMenuLateral`).
 */
export function Topbar({ onOpenSidebar, contraido, onToggleSidebar }: TopbarProps) {
  const location = useLocation();
  const user = useAuthStore((state) => state.user);
  const logout = useLogout();

  const [menuOpen, setMenuOpen] = useState(false);
  const [cambioAbierto, setCambioAbierto] = useState(false);
  const closeMenu = useCallback(() => setMenuOpen(false), []);
  const menuRef = useDismissable<HTMLDivElement>(closeMenu);

  const title = resolvePageTitle(location.pathname);

  return (
    <header className="sticky top-0 z-20 border-b border-ink-100 bg-white/85 backdrop-blur-md">
      <div className="flex h-16 items-center gap-3 px-4 sm:px-6 lg:px-8">
        <button
          type="button"
          onClick={onOpenSidebar}
          aria-label="Abrir menú"
          className="flex size-10 cursor-pointer items-center justify-center rounded-lg text-ink-600 transition-colors hover:bg-ink-100 lg:hidden"
        >
          <Menu className="size-5" />
        </button>

        <button
          type="button"
          onClick={onToggleSidebar}
          aria-label={contraido ? 'Expandir menú' : 'Contraer menú'}
          aria-expanded={!contraido}
          title={contraido ? 'Expandir menú' : 'Contraer menú'}
          className="hidden size-10 cursor-pointer items-center justify-center rounded-lg text-ink-600 transition-colors hover:bg-ink-100 lg:flex"
        >
          {contraido ? (
            <PanelLeftOpen className="size-5" aria-hidden="true" />
          ) : (
            <PanelLeftClose className="size-5" aria-hidden="true" />
          )}
        </button>

        <div className="min-w-0 flex-1">
          <h2 className="truncate text-base font-semibold tracking-tight text-ink-900 sm:text-lg">
            {title}
          </h2>
          <p className="hidden truncate text-xs text-ink-400 sm:block">
            {user?.company ? `Estación de servicio · ${user.company}` : 'Estación de servicio'}
          </p>
        </div>

        {/* Pantalla completa (la misma acción que F11) */}
        <BotonPantallaCompleta className="hidden sm:flex" />

        {/* Avisos: comprobantes rechazados, RIC atrasado, turnos abiertos… */}
        <NotificacionesMenu />

        {/* Menú de usuario */}
        <div className="relative" ref={menuRef}>
          <button
            type="button"
            onClick={() => setMenuOpen((open) => !open)}
            aria-haspopup="menu"
            aria-expanded={menuOpen}
            aria-label={`Menú de usuario: ${user?.displayName ?? 'Usuario'}`}
            className="flex cursor-pointer items-center gap-2 rounded-full py-1 pl-1 pr-2 transition-colors hover:bg-ink-100"
          >
            <Avatar name={user?.displayName ?? 'Usuario'} src={user?.photo} size="sm" />
            <ChevronDown
              aria-hidden="true"
              className={cn('size-4 text-ink-400 transition-transform', menuOpen && 'rotate-180')}
            />
          </button>

          {menuOpen ? (
            <div
              role="menu"
              className="absolute right-0 mt-2 w-64 animate-fade-up overflow-hidden rounded-card border border-ink-100 bg-white shadow-card"
            >
              <div className="border-b border-ink-100 px-4 py-3">
                <p className="truncate text-sm font-semibold text-ink-900">{user?.displayName}</p>
                <p className="truncate text-xs text-ink-400">{user?.email ?? user?.username}</p>
              </div>

              <div className="px-4 py-3 text-xs text-ink-500">
                <p className="flex flex-wrap items-center gap-2">
                  <ShieldCheck className="size-3.5 text-brand-500" aria-hidden="true" />
                  {user?.isAdmin ? 'Perfil administrador' : 'Perfil operador'}
                </p>
                <p className="mt-1.5 flex items-center gap-2">
                  <UserCog className="size-3.5 text-brand-500" aria-hidden="true" />
                  {user?.groups.length ?? 0} módulos habilitados
                </p>
              </div>

              <button
                type="button"
                role="menuitem"
                onClick={() => {
                  setMenuOpen(false);
                  setCambioAbierto(true);
                }}
                className="flex w-full cursor-pointer items-center gap-2 border-t border-ink-100 px-4 py-3 text-sm font-medium text-ink-700 transition-colors hover:bg-ink-50"
              >
                <KeyRound className="size-4" aria-hidden="true" />
                Cambiar contraseña
              </button>

              <button
                type="button"
                role="menuitem"
                onClick={() => void logout()}
                className="flex w-full cursor-pointer items-center gap-2 px-4 py-3 text-sm font-medium text-rose-600 transition-colors hover:bg-rose-50"
              >
                <LogOut className="size-4" aria-hidden="true" />
                Cerrar sesión
              </button>
            </div>
          ) : null}
        </div>

        <CambiarContrasenaDialog open={cambioAbierto} onClose={() => setCambioAbierto(false)} />
      </div>
    </header>
  );
}
